import classNames from 'classnames';
import React, {useCallback, useEffect, useReducer, useState} from 'react';
import dynamic from 'next/dynamic';
import Alert from 'react-bootstrap/Alert';

import {api} from '@utils/api';
import {product, requestData, pageUrl} from '@utils/product';
import {convertToAud} from '@utils/functions';
import Loading from '@components/Loading';
import {reducer} from '@reducers/product/list';
import {useApp} from '@context/AppProvider';
import {useCart} from '@context/CartProvider';
import StoreDetail from '@components/product/StoreDetail';

import {CategoryProvider} from './Context/Category';
import {BrandProvider} from './Context/Brand';
import {RetailerProvider} from './Context/Retailer';
import {ShippingProvider} from './Context/Shipping';
// eslint-disable-next-line import/order
import {MasterAttributeProvider} from './Context/MasterAttribute';

const Filters = dynamic(() => import('./Filters'), {
	loading: () => <p></p>,
});
const UpBlock = dynamic(() => import('./UpBlock'));
const Pagination = dynamic(() => import('./Pagination'));
const Breadcrumbs = dynamic(() => import('./Breadcrumbs'));

const ProductDetailModal = dynamic(() => import('@models/ProductDetail.modal'));

import Item from './Item';
import AuctionItem from './AuctionItem';
import AuctionTopBar from './AuctionTopBar';
import PromoItem from './PromoItem';
import LandscapeBanner from './LandscapeBanner';
const initState = {
	products: [],
	pageInfo: {
		limit: 21,
		page: 1,
		pages: 1,
		total: 0,
		skip: 0,
	},
	btFee: 0,
	goodCausePer: 0,
	stripeFee: 0,
	loading: true,
};

const NoItem = props => {
	return (
		<div className='row noFoundBlock'>
			<div className='col-md-12 text-center'>
				<Alert variant='danger' className='text-danger p-5'>
					<Alert.Heading className='text-danger'>
						Sorry, No {props.isAuction ? 'Auctions' : 'Products'} found
					</Alert.Heading>
					Please select another category or search again
				</Alert>
			</div>
		</div>
	);
};

/**
 * @currency Current Currency
 * @currencyData All Currencies
 * @productBody Auction or product Request Data
 */
const convertPricesToAUD = (currency, currencyData, productBody) => {
	if (
		currency !== 'aud' &&
		currencyData.length &&
		((productBody?.filter?.price?.min && productBody?.filter?.price?.max) ||
			(productBody?.price_from && productBody?.price_to))
	) {
		if (productBody?.filter?.price) {
			productBody.filter.price.min = convertToAud(
				productBody.filter.price.min,
				currencyData,
				currency,
			).toString();
			productBody.filter.price.max = convertToAud(
				productBody.filter.price.max,
				currencyData,
				currency,
			).toString();
		} else {
			// eslint-disable-next-line camelcase
			productBody.price_from = convertToAud(
				productBody?.price_from,
				currencyData,
				currency,
			).toString();
			// eslint-disable-next-line camelcase
			productBody.price_to = convertToAud(
				productBody?.price_to,
				currencyData,
				currency,
			).toString();
		}
	}
};

const getIndex = {
	1: 0,
	2: 2,
	0: 1,
};

function Listing(props) {
	const {styles, filters, landscapeBanner = {}} = props;
	const [state, dispatch] = useReducer(reducer, initState);
	const {
		idb: {user, origin, session, currencyData, currency, isoCode},
	} = useApp();
	const {fetchWishlistCount} = useCart();
	const [isWishlistRemoved, setisWishlistRemoved] = useState(null);
	const loadData = useCallback(async () => {
		if (!session) return;
		dispatch({
			type: 'SET_DATA',
			data: {
				...initState,
				products: [],
				loading: true,
			},
		});

		let productBody = requestData(props);
		if (user) productBody['userId'] = user._id;
		// Converting Price filters to AUD from other Currency
		convertPricesToAUD(currency, currencyData, productBody);
		const res = await api(
			{
				url: props.isAuction ? '/auction' : '/product',
				method: 'POST',
				data: {...productBody},
			},
			session,
		);
		let products = [];
		let bannerLimit =
			productBody.banners.length > 2 ? 2 : productBody.banners.length;
		if (bannerLimit > 0 || Object.keys(landscapeBanner).length) {
			let count = bannerLimit;
			let index =
				productBody.banners.length > 2 ? getIndex[productBody.page % 3] : 0;
			for (let i = 0; i < res.results.length; i++) {
				if (count > 0 && i > 2 && (i === 3 || i === 14)) {
					products.push({
						type: 'banner',
						...productBody.banners[index],
					});
					count--;
					index++;
					if (index === 3) index = 0;
				}
				if (products.length === 12 && Object.keys(landscapeBanner).length) {
					products.push({
						type: 'landscape',
						...landscapeBanner,
					});
				}
				products.push({
					type: 'product',
					...res.results[i],
				});
			}
		} else {
			products = res.results;
		}
		dispatch({
			type: 'SET_DATA',
			data: {
				products,
				pageInfo: {
					limit: res.limit,
					page: res.page,
					pages: res.pages,
					total: res.total,
					skip: res.skip,
				},
				btFee: res.default_bt_fee,
				currencyData: res.currencyData,
				goodCausePer: res.good_cause_percentage,
				stripeFee:
					isoCode.toLowerCase() === 'au'
						? res.australia_stripe_fee
						: res.international_stripe_fee,
				loading: false,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props, origin, session, currencyData]);
	useEffect(() => {
		loadData();
	}, [loadData]);

	const callBackMethodModal = async () => {
		if (isWishlistRemoved && isWishlistRemoved?.data?.wishlistData?._id) {
			fetchWishlistCount();
			let products = state.products;
			products[
				state.products.findIndex(fi => fi._id === isWishlistRemoved?.data?._id)
			]['wishlistData'] = isWishlistRemoved?.result?.wishlist?._id
				? isWishlistRemoved?.result.wishlist
				: {};
			dispatch({
				type: 'SET_DATA',
				data: {
					products: products,
				},
			});
			setisWishlistRemoved(null);
		}
		dispatch({
			type: 'SET_DATA',
			data: {
				showProductModal: false,
				modalData: {},
			},
		});
	};
	const addToWishlistOrRemove = useCallback(
		async data => {
			try {
				// if (!user) return showModel('login');
				const result = await api(
					{
						url: `/wishlist/upsert`,
						method: 'POST',
						data: {
							// eslint-disable-next-line camelcase
							product_id: data._id,
							// eslint-disable-next-line camelcase
							user_wishlist_id: data.wishlistData ? data.wishlistData._id : '',
						},
					},
					session,
				);
				if (result.status) {
					fetchWishlistCount();
					let products = state.products;
					products[state.products.findIndex(fi => fi._id === data._id)][
						'wishlistData'
					] = result.wishlist._id ? result.wishlist : {};
					dispatch({
						type: 'SET_DATA',
						data: {
							products: products,
						},
					});
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(error);
			}
		},
		[dispatch, state, session, fetchWishlistCount],
	);
	return (
		<CategoryProvider props={props}>
			<BrandProvider props={props}>
				<RetailerProvider props={props}>
					<ShippingProvider props={props}>
						<MasterAttributeProvider props={props}>
							{props?.filters?.name && (
								<>
									<div className={styles.heading}>
										<h1>
											Here are your results for : <b>{props?.filters?.name}</b>
										</h1>
									</div>
								</>
							)}
							{props.isAuction && <AuctionTopBar styles={props.styles} />}
							<div className='container'>
								<Breadcrumbs {...props} />
								{props.store && !state.loading && (
									<StoreDetail
										styles={styles}
										retailer={props?.retailer}
										store={props.store}
									/>
								)}
								<div className='row'>
									<div className='col-md-3'></div>
									<div className={`col-md-9 ${styles.listingColRight}`}>
										<div className={styles.listingRightBlock}>
											<UpBlock
												loading={state.loading}
												total={state.pageInfo.total}
												styles={styles}
												filters={filters}
												pageUrl={pageUrl(props)}
												isAuction={props.isAuction}
												category={props.category}
												brand={props.brand}
												store={props.store}
												pageUrlFn={pageUrl}
												dispatchList={dispatch}
											/>
										</div>
									</div>
								</div>
								<div className='row listing-row'>
									<div
										className={classNames('col-md-3', styles.listingColLeft)}
									>
										<Filters {...props} />
									</div>
									<div className={`col-md-9 ${styles.listingColRight}`}>
										<div className={styles.listingRightBlock}>
											{state.loading ? (
												<Loading.ProductList length={20} />
											) : state.products ? (
												<>
													<div className='row'>
														{state.products.map((p, key) => {
															if (p.type === 'landscape') {
																return (
																	<LandscapeBanner
																		key={key}
																		item={landscapeBanner}
																		styles={styles}
																	/>
																);
															}
															if (p.type === 'banner') {
																return (
																	<PromoItem
																		key={key}
																		item={p}
																		styles={styles}
																	/>
																);
															}
															if (props.isAuction) {
																return (
																	<AuctionItem
																		key={key}
																		data={p}
																		styles={styles}
																		state={state}
																	/>
																);
															}

															const data = product(
																p,
																state.currencyData,
																props,
															);
															let btFee = state.btFee;
															if (props.retailerId) {
																const r = p.retailer.find(
																	i => i._id === props.retailerId,
																);

																if (r && r.transactionChargePercentage) {
																	btFee = +r.transactionChargePercentage;
																}

																const rp = p.retailer_products.find(
																	i => i.retailer_id === props.retailerId,
																);

																if (
																	rp &&
																	rp.transaction_charge_option &&
																	rp.transaction_charge_percentage
																) {
																	btFee = +rp.transaction_charge_percentage;
																}
															} else if (p.retailer.length === 1) {
																const r = p.retailer[0];

																if (r && r.transactionChargePercentage) {
																	btFee = +r.transactionChargePercentage;
																}
															}
															return (
																<Item
																	data={data}
																	key={key}
																	addToWishlistOrRemove={addToWishlistOrRemove}
																	styles={styles}
																	state={state}
																	btFee={btFee}
																	dispatch={dispatch}
																	store={props.store}
																	retailerCount={p.retailer_products?.length}
																/>
															);
														})}
													</div>
													{state?.pageInfo?.pages > 1 && (
														<Pagination
															{...state.pageInfo}
															filters={filters}
															pageUrl={pageUrl(props)}
														/>
													)}
												</>
											) : (
												<NoItem {...props} />
											)}
										</div>
									</div>
								</div>
							</div>
							{state.showProductModal && (
								<ProductDetailModal
									product={state.modalData.product}
									brand={state.modalData.brand}
									currencyData={state.modalData.currencyData}
									mas={state.modalData.mas}
									promotions={state.modalData.promotions}
									settings={state.modalData.settings}
									queryParams={state.modalData.queryParams}
									isBrand={state.modalData.isBrand}
									removeWishlist={setisWishlistRemoved}
									store={props.store}
									close={callBackMethodModal}
								/>
							)}
						</MasterAttributeProvider>
					</ShippingProvider>
				</RetailerProvider>
			</BrandProvider>
		</CategoryProvider>
	);
}

export default Listing;
